import { buildCmsSeoFields } from "~/layers/base/utils/cmsSeoFields";

/** Storyblok components of blog stories, shared with the CMS pages. */
export const BLOG_STORY_COMPONENTS = [
	"article",
	"BlogPage",
	"blog-page",
	"BlogCategory",
	"blog-category"
];

export const isBlogStoryComponent = (component: string | undefined) =>
	BLOG_STORY_COMPONENTS.includes(component ?? "");

interface CmsSeoMetaOptions {
	/** Use the site logo as the card image when the story sets no og_image. */
	logoAsFallbackImage?: boolean;
}

/**
 * Meta tags and the OG card for a CMS page (pages/cms/[...slug].vue in base
 * and every template copy). Blog stories are skipped: they keep their SEO in
 * a lowercase `seo` field and the blog components set their own meta and OG
 * image — a card defined here would replace theirs.
 */
export const useCmsSeoMeta = (
	// Storyblok stories are untyped here, as in useCmsSchemaOrg.
	story: Record<string, any> | null | undefined,
	options: CmsSeoMetaOptions = {}
) => {
	const content = story?.content;
	if (!content || isBlogStoryComponent(content.component)) {
		return;
	}

	const firstRichText = (content.body || []).find(
		(block: { component?: string }) => block.component === "rich-text"
	);
	const fields = buildCmsSeoFields(
		content,
		firstRichText?.content ? renderRichText(firstRichText.content) || "" : ""
	);

	useSeoMeta({
		title: fields.title,
		ogTitle: fields.ogTitle,
		description: fields.description,
		ogDescription: fields.ogDescription
	});

	if (import.meta.server) {
		const logo = useAppStore().siteLogo?.filename || "";
		defineOgImageComponent("OgCms", {
			image: fields.ogImage || (options.logoAsFallbackImage ? logo : undefined),
			logo,
			title: fields.ogTitle || "",
			description: fields.description || "",
			width: 1200,
			height: 630
		});
	}
};
